<template>
  <div>
    <template v-if="!isLoading && !!sourceActivity && !!targetActivity">
      <div class="comparator-content pt-20 pb-20">
        <div class="row pr-25">
          <div class="col pl-0">
            <h5 class="fs-14 text-color">
              <b>Version {{ sourceActivity.version || 1 }}</b> (Live)
            </h5>
          </div>

          <div class="col pr-0 bl-1">
            <h5 class="fs-14 text-color">
              <b>Version {{ targetActivity.version || 1 }}</b> (Modification)
            </h5>
          </div>
        </div>

        <!-- Activity Information Block: Start -->
        <div class="comparison-data activity-configurator-step-view-mode d-block pr-10 pb-10">
          <div class="row">
            <!-- Live Activity Information -->
            <div class="col pl-0">
              <div class="detail-container pt-18 pb-18 pl-20 pr-20">
                <h6 class="fs-16 color_666669 fw-bold mb-8">
                  Activity Information
                </h6>
                <activity-details :hide-activity-settings="true" :activity="sourceActivity" />
              </div>
            </div>

            <!-- Targeted Activity Information -->
            <div class="col pr-0 bl-1">
              <div class="detail-container pt-18 pb-18 pl-20 pr-20">
                <h6 class="fs-16 color_666669 fw-bold mb-8">
                  Activity Information
                </h6>
                <activity-details :hide-activity-settings="true" :activity="targetActivity" :diff-items="diffKeys" />
              </div>
            </div>
          </div>
          <!-- Activity Information Block: End -->

          <!-- Activity Settings Block: Start -->
          <div class="row">
            <!-- Live Activity Settings -->
            <div class="col pl-0">
              <div class="detail-container pt-18 pb-18 mt-18 pl-20 pr-20">
                <h6 class="fs-16 color_666669 fw-bold mb-8">
                  Activity Settings
                </h6>
                <activity-details :hide-activity-details="true" :activity="sourceActivity" />
              </div>
            </div>

            <!-- Targeted Activity Settings -->
            <div class="col pr-0 bl-1">
              <div class="detail-container pt-18 pb-18 mt-18 pl-20 pr-20">
                <h6 class="fs-16 color_666669 fw-bold mb-8">
                  Activity Settings
                </h6>
                <activity-details :hide-activity-details="true" :activity="targetActivity" :diff-items="diffKeys" />
              </div>
            </div>
          </div>
          <!-- Activity Settings Block: End -->

          <!-- Activity questions steps Block: Start -->
          <div class="row">
            <div class="col pl-0">
              <h6 class="color_666669 fs-18 mt-34 fw-bold">Activity Steps</h6>
            </div>
            <div class="col pr-0 bl-1">
              <h6 class="color_666669 fs-18 mt-34 fw-bold">Activity Steps</h6>
            </div>
          </div>

          <template v-for="(question, index) in questionList">
            <div class="row" :key="index">
              <!-- Live Activity Question steps -->
              <div class="col pl-0 d-flex">
                <div
                  v-if="!!sourceActivity.questions[index] && sourceActivity.questions[index].type != 'SurveyCompletion'"
                  class="detail-container pt-18 pb-18 mt-18 pl-20 pr-20"
                  :class="{
                    'wong-backer': sourceActivity.questions[index].type === 'WongBaker',
                    'date-time': sourceActivity.questions[index].type === 'DateTime'
                  }"
                >
                  <component :is="`${sourceActivity.questions[index].type}Step`" :question="sourceActivity.questions[index]" :showStep="true" :activityDetails="sourceActivity" mode="VIEW" />
                </div>
              </div>

              <!-- Targeted Activity Question steps -->
              <div class="col pr-0 bl-1 d-flex">
                <template v-if="!!targetActivity.questions[index]">
                  <div
                    class="detail-container pt-18 pb-18 mt-18 pl-20 pr-20"
                    :class="{
                      changed: !!diffKeys && !!diffKeys.questions && !empty(diffKeys.questions[index]),
                      'wong-backer': targetActivity.questions[index].type === 'WongBaker',
                      'date-time': targetActivity.questions[index].type === 'DateTime'
                    }"
                  >
                    <component :is="`${targetActivity.questions[index].type}Step`" :question="targetActivity.questions[index]" :showStep="true" :activityDetails="targetActivity" mode="VIEW" />
                  </div>
                </template>
                <template v-else>
                  <div v-if="!!sourceActivity.questions[index] && sourceActivity.questions[index].type != 'SurveyCompletion'" class="detail-container changed pt-18 pb-18 mt-18 pl-20 pr-20" />
                </template>
              </div>
            </div>
          </template>
          <!-- Activity questions steps Block: Start -->
        </div>
      </div>

      <div v-if="!hidePushToLive" class="text-right pt-20">
        <b-button variant="outline-primary" size="lg" @click="pushVersionLive">
          Push Modifications Live
        </b-button>
      </div>
    </template>
    <template v-else>
      <p class="text-center">Loading...</p>
    </template>
  </div>
</template>

<script>
import { transform, isEqual, isArray, isObject, includes, isEmpty, has } from "lodash";
import ActivityDetails from "@/components/activity-library/activity-comparator/activity-details";
import BooleanStep from "@/components/activity-library/activity-steps/boolean";
import SurveyInstructionStep from "@/components/activity-library/activity-steps/instruction";
import NumberScaleStep from "@/components/activity-library/activity-steps/number-scale";
import CustomScaleStep from "@/components/activity-library/activity-steps/custom-scale";
import VASStep from "@/components/activity-library/activity-steps/vas";
import NumberStep from "@/components/activity-library/activity-steps/number-response";
import TextStep from "@/components/activity-library/activity-steps/text-response";
import DateTimeStep from "@/components/activity-library/activity-steps/date-time";
import MultiChoiceStep from "@/components/activity-library/activity-steps/multi-choice";
import ImageStep from "@/components/activity-library/activity-steps/image";
import DropDownStep from "@/components/activity-library/activity-steps/dropdown";
import MeasurementsStep from "@/components/activity-library/activity-steps/measurements";
import LogStep from "@/components/activity-library/activity-steps/log";
import ValidatedEntryStep from "@/components/activity-library/activity-steps/validated-entry";
import IndexStep from "@/components/activity-library/activity-steps/index-step";
import GpSkinStep from "@/components/activity-library/activity-steps/gp-skin";
import ImageCaptureStep from "@/components/activity-library/activity-steps/image-capture";
import WongBakerStep from "@/components/activity-library/activity-steps/wong-baker";
import MedicationSelectorStep from "@/components/activity-library/activity-steps/medication-selector";
import AudioRecordingStep from "@/components/activity-library/activity-steps/audio-recording";
import VideoRecordingStep from "@/components/activity-library/activity-steps/video-recording";
import SurveyCompletionStep from "@/components/activity-library/activity-steps/custom-completion-screen";
import { mapGetters } from "vuex";

export default {
  name: "ActivityComparator",

  components: {
    ActivityDetails,
    BooleanStep,
    SurveyInstructionStep,
    NumberScaleStep,
    CustomScaleStep,
    VASStep,
    NumberStep,
    TextStep,
    DateTimeStep,
    MultiChoiceStep,
    ImageStep,
    DropDownStep,
    MeasurementsStep,
    LogStep,
    ValidatedEntryStep,
    IndexStep,
    GpSkinStep,
    ImageCaptureStep,
    WongBakerStep,
    MedicationSelectorStep,
    AudioRecordingStep,
    VideoRecordingStep,
    SurveyCompletionStep
  },

  props: {
    sourceActivityData: {
      type: Object,
      default: null,
      validator: value => {
        if (value === null) {
          return true;
        }

        return Object.keys(value).length > 0 && value.hasOwnProperty("activityId") && value.hasOwnProperty("revisionId");
      }
    },

    targetActivityData: {
      type: Object,
      default: null,
      validator: value => {
        if (value === null) {
          return true;
        }

        return Object.keys(value).length > 0 && value.hasOwnProperty("activityId") && value.hasOwnProperty("revisionId");
      }
    },

    loadDifferences: {
      type: Boolean,
      default: false
    },

    hidePushToLive: {
      type: Boolean,
      default: false
    },

    studyId: {
      type: String,
      default: ""
    },

    notificationCount: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      sourceActivity: null,
      targetActivity: null,
      isLoading: false,
      ignoredKeys: ["created_time", "createdTime", "rows", "modified_time"],
      diffKeys: null
    };
  },

  computed: {
    ...mapGetters(["user"]),
    questionList() {
      return this.sourceActivity.questions.length > this.targetActivity.questions.length ? this.sourceActivity.questions : this.targetActivity.questions;
    },

    isParticipantActivity() {
      const activity = this.targetActivity || [];
      const matchedRole = activity.completedBy.find(userType => {
        return ["Patient/Participant", "Observer/Caregiver"].includes(userType);
      });

      return !!matchedRole;
    }
  },

  watch: {
    loadDifferences(v) {
      v && this.loadActivities();
    }
  },

  methods: {
    async loadActivities() {
      try {
        this.isLoading = true;
        const sourceActivity = await this.$api.getActivityDetails(this.sourceActivityData.activityId, this.sourceActivityData.revisionId);
        const targetActivity = await this.$api.getActivityDetails(this.targetActivityData.activityId, this.targetActivityData.revisionId);

        this.sourceActivity = sourceActivity.body.result;
        this.sourceActivity.questions = this.filterQuestions(this.sourceActivity.questions);

        this.targetActivity = targetActivity.body.result;
        this.targetActivity.questions = this.filterQuestions(this.targetActivity.questions);

        this.diffKeys = this.activityDifferences(this.sourceActivity, this.targetActivity);
        this.diffKeys = this.modifyDiffKeys(this.diffKeys);
        this.notificationCount > 0 && this.updateNotificationStatus();
      } catch (error) {
        const errorMsg = error.body ? error.body.message : "Error while getting activity details";
        this.$toastr.error(errorMsg);
      } finally {
        this.isLoading = false;
      }
    },

    activityDifferences(live, targeted) {
      this.modifyTargetedActivity(live, targeted);

      return this.changes(targeted, live);
    },

    async updateNotificationStatus() {
      try {
        const payload = {
          revisionId: this.targetActivityData.revisionId,
          viewedBy: this.user.id
        };

        const activityId = this.targetActivityData.activityId;
        const studyId = this.studyId;

        const apiResponse = await this.$api.markNotificationStatusAsRead(studyId, activityId, payload);
        const apiResponseData = apiResponse.body;
        this.$emit("updateCount", apiResponseData);
      } catch (error) {
        const errorMsg = error.body ? error.body.message : "Error while updating notification status.";
        this.$toastr.error(errorMsg);
      }
    },

    changes(targeted, live) {
      let arrayIndexCounter = 0;
      const self = this;
      return transform(targeted, function(result, value, key) {
        if ((value === true || value === false) && !has(live, key)) {
          live[key] = false;
        }
        if (!isEqual(value, live[key]) && !includes(self.ignoredKeys, key)) {
          const resultKey = isArray(live) ? arrayIndexCounter++ : key;
          result[resultKey] = isObject(value) && isObject(live[key]) ? self.changes(value, live[key]) : value;
        }
      });
    },

    filterQuestions(questions) {
      return questions
        .filter(question => {
          return question.type != "SurveyCompletion";
        })
        .map(question => {
          question.stepSequence = question.sequence;
          return question;
        });
    },

    empty(v) {
      return isEmpty(v);
    },

    pushVersionLive() {
      this.$set(this.targetActivity, "isSitefacing", !this.isParticipantActivity);
      this.$set(this.targetActivity, "revisionId", this.targetActivityData.revisionId);
      this.$emit("pushLive", this.targetActivity, 1);
    },

    modifyDiffKeys(diffKeys) {
      const self = this;
      return transform(diffKeys, function(result, value, key) {
        if (isArray(value)) {
          const modifiedArray = self.removeBlankEntries(value);
          result[key] = modifiedArray.map(activityStep => {
            for (const property in activityStep) {
              if (isArray(activityStep[property]) && activityStep[property].length === 0) {
                delete activityStep[property];
              }
            }
            return activityStep;
          });
        } else {
          result[key] = value;
        }
      });
    },

    removeBlankEntries(arr) {
      const self = this;
      return transform(arr, function(result, value, key) {
        if (!isEmpty(value)) {
          result[key] = isObject(value) ? self.removeBlankEntries(value) : value;
        }
      });
    },

    modifyTargetedActivity(live, targeted) {
      Object.keys(live).forEach(key => {
        if ((live[key] === true || live[key] === false) && !has(targeted, key)) {
          targeted[key] = false;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.bl-1 {
  border-left: 1px solid rgba(211, 222, 227, 0.4);
}
.comparison-data {
  height: 511px;
  overflow-x: hidden;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 0.9375rem;
    background: #e2e7ea;
  }

  &::-webkit-scrollbar-track {
    background: #e2e7ea;
  }

  &::-webkit-scrollbar-thumb {
    width: 0.475rem;
    border-radius: 10px;
    border: 3.7px solid transparent;
    background-color: #b0bac9;
    background-clip: content-box;
  }

  .detail-container {
    background: #f5f8fa;
    border: solid 1px #d3dee3;
    border-radius: 4px;
    flex: 1 1;

    &.changed {
      background: rgba(249, 233, 120, 0.4);
      border-color: rgb(249, 233, 120);
    }

    /deep/ .question-container {
      display: flex;
      align-items: baseline;

      .step {
        font-weight: bold;
        &:after {
          content: " :";
        }
      }

      p {
        &.question_body {
          margin-left: 5px;
        }
      }

      .horizontal-vue-slider {
        margin-top: 20px;
      }
    }

    /deep/ .scaleLabels-vas {
      display: flex;
      justify-content: space-between;
    }

    /deep/ .input-text {
      &:not(.table) {
        width: 100%;
        outline: none;
        border: 1px solid #ccc;
        height: 35px;
        text-indent: 10px;
        flex: 1 1 auto;
        max-width: 40%;
      }

      &.readonly {
        background: #f7f7f7;
      }
    }

    /deep/ .wong-baker-smiley {
      max-width: 100%;
      width: 100%;
      height: auto;
    }

    &.wong-backer {
      /deep/ .col-md-6 {
        max-width: 100%;
        flex: 1;
      }
    }

    /deep/ .horizontal-vue-slider {
      margin-top: 20px;
    }

    &.date-time {
      /deep/ .option-section {
        display: flex;
        flex-wrap: wrap;

        .question-container {
          flex: 1 1 100%;
        }
        .input-text {
          max-width: 100%;
        }
      }
    }
  }
}
</style>
